import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../../onboardingV2.locale';
import {
    Root,
    Caption,
    Phone,
    Screen,
    UrlBar,
    Scroll,
    Banner,
    Head,
    Avatar,
    NameBlock,
    Name,
    Status,
    Desc,
    Cats,
    Cat,
    SubPills,
    Grid,
    Cell,
    Thumb,
    Plus,
    Badge,
    Info,
    Detail,
    GhostLine,
} from './StoreLivePreview.styles';

/**
 * Desktop-only live preview of the storefront (https://{url}.ecometri.shop), filled from the
 * onboarding form as the user types. Mirrors the real store: banner, circular logo, name with the
 * "Tienda WhatsApp" status, description, circular category row and a product grid with "Ver
 * Detalles" (every product added so far, at least two cells). Wholesale adds the volume-price hint.
 */
const MIN_CELLS = 2;
const MAX_CELLS = 6;

const StoreLivePreview = ({
    storeName,
    displayUrl,
    description,
    categories = [],
    products = [],
    bannerUrl,
    logoUrl,
    currency,
    locale,
    storeType,
    isWholesale = false,
}) => {
    const { t } = useTranslation();
    const name = (storeName || '').trim() || t('SignUpV2.preview.placeholderName');
    const initial = name.charAt(0);
    const url = displayUrl || t('SignUpV2.preview.placeholderUrl');
    const picked = Array.isArray(categories) ? categories : [];
    // Like the store: opening a category shows its subcategories as pills. Preview the first
    // category that has subcategories as if the customer had tapped it.
    const openCat = picked.find(
        (category) => Array.isArray(category.subcategories) && category.subcategories.length > 0
    );

    const shown = (Array.isArray(products) ? products : []).slice(0, MAX_CELLS);
    const cells = [...shown, ...Array(Math.max(0, MIN_CELLS - shown.length)).fill(null)];
    const money = (value) => formatMoney(Number(value), currency, locale);

    return (
        <Root aria-hidden="true">
            <Caption className="sv2-caption">{t('SignUpV2.preview.caption')}</Caption>
            <Phone>
                <Screen>
                    <UrlBar>
                        <i className="ri-global-line" />
                        <span>{url}</span>
                    </UrlBar>
                    <Scroll>
                        <Banner src={bannerUrl} />
                        <Head>
                            <Avatar src={logoUrl}>{!logoUrl && initial}</Avatar>
                            <NameBlock>
                                <Name>{name}</Name>
                                <Status>
                                    {storeType === 'both'
                                        ? t('SignUpV2.preview.bothTag')
                                        : storeType === 'wholesale'
                                          ? t('SignUpV2.preview.wholesaleTag')
                                          : t('SignUpV2.preview.retailTag')}
                                </Status>
                            </NameBlock>
                        </Head>

                        {description ? (
                            <Desc>{description}</Desc>
                        ) : (
                            <Desc as="div">
                                <GhostLine w="90%" mt="0" />
                                <GhostLine w="55%" />
                            </Desc>
                        )}

                        <Cats>
                            <Cat active={!openCat}>
                                <span className="circle">
                                    <i className="ri-apps-2-line" aria-hidden="true" />
                                </span>
                                <span className="label">{t('SignUpV2.preview.allProducts')}</span>
                            </Cat>
                            {picked.map((category) => (
                                <Cat key={category.id} active={openCat && openCat.id === category.id}>
                                    <span
                                        className="circle"
                                        style={
                                            category.photoUrl
                                                ? {
                                                      backgroundImage: `url(${category.photoUrl})`,
                                                      backgroundSize: 'cover',
                                                      backgroundPosition: 'center',
                                                  }
                                                : undefined
                                        }
                                    >
                                        {!category.photoUrl && (
                                            <i className={category.icon} aria-hidden="true" />
                                        )}
                                    </span>
                                    <span className="label">{category.label}</span>
                                </Cat>
                            ))}
                        </Cats>

                        {openCat && (
                            <SubPills>
                                <span className="pill active">{t('SignUpV2.preview.allProducts')}</span>
                                {openCat.subcategories.map((sub) => (
                                    <span className="pill" key={sub.id}>
                                        {sub.label}
                                    </span>
                                ))}
                            </SubPills>
                        )}

                        <Grid>
                            {cells.map((product, index) => {
                                const hasProduct = !!(
                                    product &&
                                    (product.name || (product.price != null && product.price !== ''))
                                );
                                const price =
                                    product && product.price != null && product.price !== ''
                                        ? money(product.price)
                                        : null;
                                const tierHint =
                                    isWholesale && product && product.tierMinQty && product.tierPrice
                                        ? t('SignUpV2.preview.tierHint', {
                                              qty: product.tierMinQty,
                                              price: money(product.tierPrice),
                                          })
                                        : null;
                                return (
                                    <Cell key={index}>
                                        <Thumb src={product && product.photoUrl}>
                                            {product && !product.photoUrl && (
                                                <i className="ri-image-line" aria-hidden="true" />
                                            )}
                                            <Plus aria-hidden="true">+</Plus>
                                            {tierHint && <Badge>{t('SignUpV2.preview.wholesaleShort')}</Badge>}
                                        </Thumb>
                                        <Info>
                                            {hasProduct ? (
                                                <>
                                                    <div className="pname">
                                                        {product.name || t('SignUpV2.preview.yourProduct')}
                                                    </div>
                                                    {price ? (
                                                        <div className="price">{price}</div>
                                                    ) : (
                                                        <GhostLine w="50%" />
                                                    )}
                                                    {tierHint && <div className="tier">{tierHint}</div>}
                                                </>
                                            ) : (
                                                <>
                                                    <GhostLine w="85%" mt="2px" />
                                                    <GhostLine w="45%" />
                                                </>
                                            )}
                                        </Info>
                                        <Detail>{t('SignUpV2.preview.viewDetails')}</Detail>
                                    </Cell>
                                );
                            })}
                        </Grid>
                    </Scroll>
                </Screen>
            </Phone>
        </Root>
    );
};

export default StoreLivePreview;
